import { useQuery } from '@apollo/client';
import { useContext, useMemo } from 'react';

import { combineMemberFragments } from '@utils/graphql';

import { useAuth } from '@providers/AuthProvider';
import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import {
	membershipName,
	membership as membershipQuery,
	queryName,
} from '@graphql/fragments/membership';
import CURRENT_MEMBER_WITH_CANCELED_SUBSCRIPTIONS from '@graphql/queries/currentMemberWithCanceledSubscriptions.gql';
import CURRENT_MEMBER_WITH_SUBSCRIPTIONS from '@graphql/queries/currentMemberWithSubscriptions.gql';

import { IAuthenticatedMember, SubscriptionInfo, SubscriptionType } from './useMemberInfo';

export type OldSubscriptionInfo = {
	subscriptionStatus: string;
	subscriptionType: string;
	subscriptionTier: string;
	requiresAvVisit: boolean;
};

export interface ICurrentMemberWithSubscriptions {
	currentMember: {
		email: string;
		stateOfResidence: string;
		memberSubscriptions: {
			nodes: OldSubscriptionInfo[];
		};
	};
}

type UseSubscriptionInfo = (subscriptionType?: string) => {
	loading: boolean;
	subscriptionInfo?: OldSubscriptionInfo | null;
	canceledSubscriptionInfo?: OldSubscriptionInfo | null;
	allSubscriptions?: OldSubscriptionInfo[] | null;
	membership: SubscriptionInfo | null;
};

const useSubscriptionInfo: UseSubscriptionInfo = (subscriptionType?: string) => {
	const { loggedIn } = useAuth();

	const {
		loading: activeLoading,
		error: activeError,
		data: activeData,
	} = useQuery<ICurrentMemberWithSubscriptions>(CURRENT_MEMBER_WITH_SUBSCRIPTIONS, {
		skip: !loggedIn,
		fetchPolicy: 'no-cache',
	});

	const {
		loading: canceledLoading,
		error: canceledError,
		data: canceledData,
	} = useQuery<ICurrentMemberWithSubscriptions>(CURRENT_MEMBER_WITH_CANCELED_SUBSCRIPTIONS, {
		skip: !loggedIn,
		fetchPolicy: 'no-cache',
	});

	const { client: graphQLv2Client } = useContext(GraphQLv2Context);
	const {
		loading: membershipLoading,
		data: membershipData,
		error: membershipError,
	} = useQuery<IAuthenticatedMember<SubscriptionInfo>>(
		combineMemberFragments([membershipName], [membershipQuery], queryName),
		{
			client: graphQLv2Client,
		}
	);

	const membership: SubscriptionInfo | null = useMemo(() => {
		if (membershipLoading || membershipError) {
			return null;
		}
		if (
			membershipData?.authenticatedMember?.membershipSubscription?.type !==
			SubscriptionType.membership
		) {
			return null;
		}

		return membershipData?.authenticatedMember;
	}, [membershipLoading, membershipData, membershipError]);

	const subscriptionInfo: OldSubscriptionInfo | undefined | null = useMemo(() => {
		if (activeLoading || activeError) {
			return null;
		}

		return activeData?.currentMember.memberSubscriptions.nodes.find(
			(subscription) => subscription.subscriptionType === subscriptionType
		);
	}, [activeLoading, activeError, activeData, subscriptionType]);

	const allSubscriptions: OldSubscriptionInfo[] | undefined | null = useMemo(() => {
		if (activeLoading || activeError) {
			return null;
		}

		return activeData?.currentMember.memberSubscriptions.nodes;
	}, [activeLoading, activeError, activeData]);

	const canceledSubscriptionInfo: OldSubscriptionInfo | undefined | null = useMemo(() => {
		if (canceledLoading || canceledError) {
			return null;
		}

		return canceledData?.currentMember.memberSubscriptions.nodes.find(
			(subscription) => subscription.subscriptionType === subscriptionType
		);
	}, [canceledLoading, canceledError, canceledData, subscriptionType]);

	return {
		loading: activeLoading || canceledLoading || membershipLoading,
		subscriptionInfo,
		canceledSubscriptionInfo,
		allSubscriptions,
		membership,
	};
};

export default useSubscriptionInfo;
